// src/components/SaleCancelForm/parts/SaleFoundCodeBlock.tsx
import React, { useMemo, useState } from 'react';
import { Copy, Check } from 'lucide-react';

type Props = {
  code?: string | null;
  truncateAt?: number;   // caracteres a mostrar antes de truncar (por defecto 28)
  compact?: boolean;     // reduce paddings
};

const SaleFoundCodeBlock: React.FC<Props> = ({ code, truncateAt = 28, compact = false }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const display = useMemo(() => {
    if (!code) return '—';
    if (expanded || code.length <= truncateAt) return code;
    return `${code.slice(0, truncateAt)}…`;
  }, [code, expanded, truncateAt]);

  const handleCopy = async () => {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  return (
    <div className="sm:col-span-2 lg:col-span-3">
      <div className="flex flex-col gap-1">
        <div className="text-body dark:text-bodydark font-medium">Código de generación</div>

        <div
          className={[
            'flex items-center gap-2 rounded-xl border',
            'border-stroke dark:border-strokedark',
            'bg-slate-50 dark:bg-slate-800/40',
            compact ? 'px-2 py-1.5' : 'px-3 py-2',
          ].join(' ')}
        >
          <code
            className={[
              'font-mono text-xs sm:text-[13px] break-all',
              'text-body dark:text-bodydark flex-1',
            ].join(' ')}
            title={code ?? undefined}
          >
            {display}
          </code>

          {/* Toggle expandir/contraer si está truncado */}
          {code && code.length > truncateAt && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs px-2 py-1 rounded-md border border-stroke dark:border-strokedark hover:bg-stroke/20 dark:hover:bg-strokedark/20 transition"
            >
              {expanded ? 'Ocultar' : 'Ver todo'}
            </button>
          )}

          {/* Copiar */}
          <button
            type="button"
            onClick={handleCopy}
            className={[
              'inline-flex items-center gap-1 text-xs',
              'rounded-md border border-stroke dark:border-strokedark',
              'px-2 py-1 hover:bg-stroke/20 dark:hover:bg-strokedark/20 transition',
            ].join(' ')}
            title="Copiar código"
            aria-label="Copiar código de generación"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaleFoundCodeBlock;
